import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, CheckCircle, Circle, Image, FileText, Tag, MapPin, DollarSign, ExternalLink, Trophy, AlertCircle, User, Phone, Mail, Compass, Clock } from 'lucide-react';

const ProviderOnboarding = ({ user, profile, onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);

  useEffect(() => {
    // Check if onboarding was already completed
    const onboardingCompleted = localStorage.getItem(`provider_onboarding_${user?.id}`);
    if (!onboardingCompleted && user?.role === 'provider') {
      setIsVisible(true);
    }
  }, [user]);

  const steps = [
    {
      title: 'Willkommen als Dienstleister! 🎯',
      description: 'Großartig, dass du EventConnect nutzt! Wir helfen dir, neue Kunden zu gewinnen und dein Business zu wachsen.',
      icon: <Trophy className="w-12 h-12 text-yellow-500" />
    },
    {
      title: 'Vervollständige dein Profil 📋',
      description: 'Ein vollständiges Profil erhöht deine Sichtbarkeit um bis zu 300% und führt zu mehr Anfragen. Lass uns dein Profil optimieren!',
      icon: <CheckCircle className="w-12 h-12 text-green-500" />
    },
    {
      title: 'Wähle dein Abo 💳',
      description: 'Mit einem aktiven Abo wird dein Profil für Kunden sichtbar. Teste 30 Tage kostenlos!',
      icon: <DollarSign className="w-12 h-12 text-white/900" />
    },
    {
      title: 'Erhalte Anfragen 📨',
      description: 'Sobald dein Profil live ist, erhältst du qualifizierte Anfragen von Kunden. Antworte schnell für beste Ergebnisse!',
      icon: <ExternalLink className="w-12 h-12 text-purple-500" />
    }
  ];

  // Calculate profile completeness (includes all visibility requirements)
  const calculateCompleteness = () => {
    if (!profile) return 0;

    let completed = 0;
    const total = 15; // All required fields for visibility

    // Required visibility fields
    if (profile.company_name) completed++;
    if (profile.short_description) completed++;
    if (profile.long_description) completed++;
    if (profile.categories && profile.categories.length > 0) completed++;
    if (profile.location_city && profile.location_zip && profile.location_state) completed++;
    if (profile.contact_person) completed++;
    if (profile.service_radius_km && profile.service_radius_km > 0) completed++;
    if (profile.phone) completed++;
    if (profile.email) completed++;
    if (profile.base_price_from) completed++;
    if (profile.years_of_experience && profile.years_of_experience > 0) completed++;

    // Optional but nice to have
    if (profile.images && profile.images.length > 0) completed++;
    if (profile.website_link || profile.instagram_link) completed++;
    if (profile.subscription_status && profile.subscription_status !== 'none') completed++;

    return Math.round((completed / total) * 100);
  };

  const completeness = calculateCompleteness();

  const checklist = [
    {
      id: 'company_name',
      label: 'Firmenname',
      completed: !!profile?.company_name,
      icon: <Tag className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        // Navigate after state update
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'contact_person',
      label: 'Ansprechpartner',
      completed: !!profile?.contact_person,
      icon: <User className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'descriptions',
      label: 'Beschreibungen (kurz & lang)',
      completed: !!profile?.short_description && !!profile?.long_description,
      icon: <FileText className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'categories',
      label: 'Kategorien & Leistungen',
      completed: profile?.categories && profile.categories.length > 0,
      icon: <Tag className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'location',
      label: 'Standort (Stadt, PLZ & Bundesland)',
      completed: !!profile?.location_city && !!profile?.location_zip && !!profile?.location_state,
      icon: <MapPin className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'service_radius',
      label: 'Service-Radius',
      completed: profile?.service_radius_km && profile.service_radius_km > 0,
      icon: <Compass className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'phone',
      label: 'Telefon',
      completed: !!profile?.phone,
      icon: <Phone className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'email',
      label: 'E-Mail (öffentlich)',
      completed: !!profile?.email,
      icon: <Mail className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'base_price',
      label: 'Basis-Preis',
      completed: !!profile?.base_price_from,
      icon: <DollarSign className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'years_of_experience',
      label: 'Jahre Erfahrung',
      completed: profile?.years_of_experience && profile.years_of_experience > 0,
      icon: <Clock className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'images',
      label: 'Portfolio-Bilder',
      completed: profile?.images && profile.images.length > 0,
      icon: <Image className="w-5 h-5" />,
      required: false,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'social_links',
      label: 'Website oder Instagram',
      completed: !!profile?.website_link || !!profile?.instagram_link,
      icon: <ExternalLink className="w-5 h-5" />,
      required: false,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/provider/onboarding'), 0);
      }
    },
    {
      id: 'subscription',
      label: 'Abo abschließen',
      completed: profile?.subscription_status && profile.subscription_status !== 'none',
      icon: <CheckCircle className="w-5 h-5" />,
      required: true,
      action: () => {
        handleComplete();
        setTimeout(() => navigate('/pricing'), 0);
      }
    }
  ];

  const completedCount = checklist.filter(item => item.completed).length;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowChecklist(true);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(`provider_onboarding_${user?.id}`, 'completed');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  const step = steps[currentStep];

  // Checklist View
  if (showChecklist) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full p-4 sm:p-8 relative max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
          <button
            onClick={handleComplete}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="mb-4 sm:mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Profil-Checkliste</h2>
            <p className="text-gray-600">Vervollständige dein Profil für maximale Sichtbarkeit</p>
          </div>

          {/* Progress Circle */}
          <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 p-4 sm:p-6 bg-gradient-to-br from-[#FFF0D1]/30 to-[#F4F2F2] rounded-2xl">
            <div className="relative">
              <svg className="w-20 h-20 sm:w-24 sm:h-24 transform -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="44"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  className="text-gray-200"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="44"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 44}`}
                  strokeDashoffset={`${2 * Math.PI * 44 * (1 - completeness / 100)}`}
                  className="text-[#2B2142] transition-all duration-500"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl sm:text-2xl font-bold text-gray-900">{completeness}%</span>
              </div>
            </div>
            <div className="text-center sm:text-left">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">
                Profilstärke: {completeness < 50 ? 'Niedrig' : completeness < 80 ? 'Mittel' : 'Hoch'}
              </h3>
              <p className="text-gray-600">
                {completedCount} von {checklist.length} Schritte abgeschlossen
              </p>
              {completeness < 100 && (
                <div className="mt-2 flex items-center gap-2 text-sm text-orange-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>Vervollständige dein Profil für maximale Reichweite!</span>
                </div>
              )}
            </div>
          </div>

          {/* Checklist Items */}
          <div className="space-y-3">
            {checklist.map((item) => (
              <div
                key={item.id}
                className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 transition-all ${
                  item.completed
                    ? 'bg-green-50 border-green-200'
                    : 'bg-white border-gray-200 hover:border-[#2B2142] cursor-pointer'
                }`}
                onClick={() => !item.completed && item.action && item.action()}
              >
                <div className={`flex-shrink-0 ${item.completed ? 'text-green-600' : 'text-gray-400'}`}>
                  {item.completed ? (
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                  ) : (
                    <Circle className="w-5 h-5 sm:w-6 sm:h-6" />
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className="flex-shrink-0">{item.icon}</span>
                    <span className={`font-semibold text-sm sm:text-base ${item.completed ? 'text-green-900' : 'text-gray-900'}`}>
                      {item.label}
                    </span>
                  </div>
                </div>

                {!item.completed && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      item.action && item.action();
                    }}
                    className="px-2.5 sm:px-4 py-1.5 sm:py-2 bg-[#2B2142] hover:bg-[#1E1730] text-white text-xs sm:text-sm font-semibold rounded-lg transition-colors flex-shrink-0 whitespace-nowrap"
                  >
                    Bearbeiten
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Action Button */}
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              onClick={handleComplete}
              className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
            >
              Später fortsetzen
            </button>
            <button
              onClick={() => {
                handleComplete();
                setTimeout(() => navigate('/provider/onboarding'), 0);
              }}
              className="flex-1 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
            >
              Profil bearbeiten
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Onboarding Steps View
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8 relative">
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex gap-2 mb-2">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`flex-1 h-2 rounded-full transition-all ${
                  idx <= currentStep ? 'bg-[#2B2142]' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-gray-600 text-right">
            Schritt {currentStep + 1} von {steps.length}
          </p>
        </div>

        {/* Content */}
        <div className="text-center">
          <div className="mb-6 flex justify-center">
            {step.icon}
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{step.title}</h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">{step.description}</p>

          {/* Actions */}
          <div className="flex gap-4">
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
              >
                Zurück
              </button>
            )}
            
            <button
              onClick={handleNext}
              className="flex-1 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
            >
              {currentStep === steps.length - 1 ? 'Checkliste ansehen' : 'Weiter'}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {currentStep === 0 && (
            <button
              onClick={handleSkip}
              className="mt-4 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Tour überspringen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderOnboarding;
